import { can, type CurrentUser } from '@/react/lib/capabilities';

import { NAV_CONFIG } from './navConfig';
import type {
  ExpandableChild,
  ExpandableItem,
  NavConfig,
  NavFeature,
  NavItem,
  NavSection,
} from './types';

// A disabled child with no capability requirement is a "coming soon"
// placeholder.
function isPlaceholder(child: ExpandableChild): boolean {
  return child.disabled === true && !child.requiredCapability;
}

function visibleChildren(
  item: ExpandableItem,
  user: CurrentUser | undefined,
): ExpandableChild[] {
  const granted = item.children.filter((child) => {
    if (isPlaceholder(child)) return true;
    return !child.requiredCapability || can(user, child.requiredCapability);
  });

  const real = granted.filter((child) => !isPlaceholder(child));

  // Placeholders only appear once a real, permitted sibling is visible (Menus).
  if (item.placeholdersNeedRealSibling && real.length === 0) {
    return [];
  }
  return granted;
}

function visibleItems(
  items: readonly NavItem[],
  user: CurrentUser | undefined,
  enabledFeatures: readonly NavFeature[],
): NavItem[] {
  const result: NavItem[] = [];
  for (const item of items) {
    if (item.kind === 'react-mount') {
      result.push(item);
      continue;
    }
    if (item.requiredFeature && !enabledFeatures.includes(item.requiredFeature)) {
      continue;
    }
    if (item.kind === 'link') {
      if (!item.requiredCapability || can(user, item.requiredCapability)) {
        result.push(item);
      }
      continue;
    }
    // expandable
    const children = visibleChildren(item, user);
    const hasReal = children.some((c) => !isPlaceholder(c));
    if (item.hideWhenEmpty && !hasReal) {
      continue;
    }
    result.push({ ...item, children });
  }
  return result;
}

// Resolve the static NAV_CONFIG against the signed-in user's capabilities and
// the enabled feature flags, returning only the sections/items/children they
// may see. No flags passed means none are on (fail closed). This is UX gating
// only — every route is still enforced server-side by its Pundit policy.
export function selectVisibleNav(
  user: CurrentUser | undefined,
  enabledFeatures: readonly NavFeature[] = [],
  config: NavConfig = NAV_CONFIG,
): NavSection[] {
  const sections: NavSection[] = [];
  for (const section of config) {
    if (section.requiredCapability && !can(user, section.requiredCapability)) {
      continue;
    }
    const items = visibleItems(section.items, user, enabledFeatures);
    if (section.hideWhenEmpty && items.length === 0) {
      continue;
    }
    sections.push({ ...section, items });
  }
  return sections;
}
